import React, { FC, useEffect, useState } from "react";
import ActivityFrame from "../../activity-frame/activity-frame";
import { TableContextProvider } from "../../../contexts/table/TableContextProvider";
import TableTab from "../../tabs/table-tab";
import { ListQuery } from "../../../models/ListQuery";
import { ListData } from "../../../models/ListData";
import { appendFilter } from "../../../utilities/filters";
import { getEventTypes } from "../../../services/eventTypeService";
import { useLocale } from "../../../contexts/localeContext";
import EventTypeColumns from "../../../models/ColumnDefinitions/EventTypeColumns";
import EventTypeSearch from "../../../models/search/EventTypeSearch";
import { DEF_PG_SIZE_WIZARD } from "../../../constants/maximum-page-size";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import AssignWizard from "../../assignWizard/AssignWizard";
import { Lookup } from "../../../models/Lookup";
import { useFormikContext } from "formik";
import { FILTER_EVENTS, MAX_RECORDS } from "../../../constants/notification-key";
import {  handleManage } from "../../../services/notificationService";
import { toast } from "react-toastify";
import { useDialog } from "../../dialog/DialogContext";
import { getErrorMessage } from "../../../services/utilities/ServiceError";
import { SettingData } from "../../../models/SettingData";


interface EventDisplayProps {
    reloadPage: () => void;
    currentItem: any;
    id: string;
}
const EventDisplay: FC<EventDisplayProps> = ({ reloadPage, currentItem, id }) => {
    const [busy, setBusy] = useState(false);
    const { translate } = useLocale();
    const { values } = useFormikContext<any>();
    const [dataRefresh, setDataRefresh] = useState(() => (dataMethod?: (query?: ListQuery) => Promise<ListData>) => { return });
    const manualDataRefresh = (refreshData: (dataMethod?: (query?: ListQuery) => Promise<ListData>) => void) => {
        setDataRefresh(() => refreshData);
    }

    const [showManagedModal, setShowManagedModal] = useState(false);
    const [lookup, setLookup] = useState<Lookup[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [oldSelectedIds, setOldSelectedIds] = useState<string[]>([]);
    const dialog = useDialog();
    let settingDataToRemove: string[] = [];

    const getDataMethod = async (listParameters?: ListQuery) => {
        let data = await getEventTypes(translate, {
            ...listParameters,
            filters: appendFilter(
                currentItem.filterEvents?.toString(),
                "eventTypeId",
                "id",
                listParameters?.filters ?? [],
                "=in=")
        })
        return data;
    }

    useEffect(() => {
        dataRefresh((listParameters?: ListQuery) => getDataMethod(listParameters));
    }, [currentItem.filterEvents])

    const manageEvents = async () => {
        setBusy(true);

        settingDataToRemove = [];
        const newSelectedIds = selectedIds.filter((value) => value !== "");
        setOldSelectedIds(newSelectedIds);

        const toBeRmoved = oldSelectedIds.filter((value) => {
            if (!newSelectedIds.includes(value)) {
                return value
            }
        });

        const toBeAdded = newSelectedIds.filter((value) => {
            if (!oldSelectedIds.includes(value)) {
                return value;
            }
        });

        const settingDataToAdd:SettingData[] =  toBeAdded.map((id) => ({
            sdValstring: id,
            eSysnameLookup: FILTER_EVENTS,
            relationship:[
                {
                    id: values.vruleTriggerEventId,
                    type: "ruleTriggerGroup",
                    relationType:"vruleTriggerGroup"
                },
                {
                    id: id,
                    type: "eventType",
                }
            ]
        }))

        if (toBeRmoved.length > 0) {
            const filteredOutSettingData = values.settingDatasRef.filter((ref) => ref.elementSysname === FILTER_EVENTS && toBeRmoved.includes(ref.sdValstring));
            filteredOutSettingData.map((remove) => {
                settingDataToRemove.push(remove.id)
            })
        }


        if (newSelectedIds.length < 1) {
            toast.error(translate.byKey(TranslationKeys.atLeastOneEventNeedsToBeSelected));
            setBusy(false);
        }
        else {
            handleManage(settingDataToAdd,settingDataToRemove).then(() => {
                toast.success(translate.byKey(TranslationKeys.recordUpdated));
                reloadPage();
            }).catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
            }).finally(() => {
                setBusy(false);
            })
        }
    }

    const buildAssignedEvents = async () => {
        try {
            const events = ((await getDataMethod()) as any).rows;
            setSelectedIds(events.map((event) => event.id.toString()));
            setOldSelectedIds(
                events.map((company) => company.id.toString())
            );
        } catch (error) {
            toast.warn(getErrorMessage(error));
        }
    };

    useEffect(() => {
        buildAssignedEvents();
    }, [showManagedModal]);

    const validateSelectedIds = (ids: string[]) => {
        if (ids.length >= MAX_RECORDS) {
            toast.error(translate.byKey(TranslationKeys.aMaximimumOf20RecordsCanBeSelected));
            setSelectedIds([...selectedIds])
        } else {
            setSelectedIds([...ids])
        }
    }

    return (<>
        <ActivityFrame busy={busy} className='flex flex-col flex-grow bg-white'>
            <div className="w-full flex flex-col">
                <TableContextProvider
                    dataMethod={getDataMethod}
                    useTable
                    usePagination
                    useFilter
                    useSearch
                    useSort
                    tableDescriptor={EventTypeColumns}
                    searchDescriptor={EventTypeSearch}
                    maxPageSize={DEF_PG_SIZE_WIZARD}
                    manualDataRefresh={manualDataRefresh}
                    canExportReport={false}>
                    <TableTab
                        title={translate.byKey(TranslationKeys.events)}
                        selectRows={false}
                        tableId={'eventBasedTable'}
                        cyName="eventDisplay">
                        <button type="button" className="flex px-4 bg-lime rounded-lg shadow text-white text-sm items-center"
                            onClick={() => setShowManagedModal(true)}>
                            {translate.byKey(TranslationKeys.manage)}
                        </button>
                    </TableTab>
                </TableContextProvider>
            </div>

            <AssignWizard
                isOpen={showManagedModal}
                onClose={() => setShowManagedModal(false)}
                title={translate.byKey(TranslationKeys.event)}
                dataMethod={(listParameters) => getEventTypes(translate,listParameters)}
                lookup={lookup}
                columnDescriptors={{
                    columns: EventTypeColumns,
                    searchColumns: EventTypeSearch,
                }}
                handleOnSubmit={manageEvents}
                selectedIds={selectedIds}
                setSelectedIds={validateSelectedIds}
            />
        </ActivityFrame>
    </>)
}
export default EventDisplay;